import React, { useState, useEffect } from 'react';
import { ArrowsClockwise, SpinnerGap } from 'phosphor-react';
import { useFightData } from '../contexts/FightDataContext';
import FightCard from './FightCard';
import ClickableElement from './ClickableElement';
import Toast from './Toast';

interface FightListProps {
  onFighterSelect: (index: number) => void;
}

const FightList: React.FC<FightListProps> = ({ onFighterSelect }) => {
  const { 
    isRefreshing, 
    refreshData, 
    eventName,
    fightPredictions,
    selectedFightIndex,
    setSelectedFightIndex,
    isToastVisible,
    toastProgress,
    toastMessage,
    dismissToast
  } = useFightData();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFightSelect = (index: number) => {
    setSelectedFightIndex(index);
    onFighterSelect(index);
  };

  return (
    <div className={`bg-white-only border-r-0 md:border-r border-border dark:border-dark-border w-full md:w-90 ${isMobile ? 'h-full overflow-y-auto' : 'h-full overflow-y-auto'}`}>
      <div className="p-4 md:p-6 relative h-full">
        <div className="flex items-center justify-between mb-4 pt-3">
          <h2 className="text-base font-medium text-text-primary dark:text-dark-text-primary">
            {eventName || "Upcoming UFC Event"}
          </h2>
          <div className="relative group">
            <ClickableElement
              as="button" 
              onClick={refreshData}
              disabled={isRefreshing}
              className={`p-1.5 rounded-full hover:bg-zinc-150 dark:hover:bg-zinc-800 text-text-secondary dark:text-dark-text-secondary active:scale-95 ${
                isRefreshing ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isRefreshing ? (
                <SpinnerGap className="h-5 w-5 animate-spin" weight="regular" />
              ) : (
                <ArrowsClockwise className="h-5 w-5" weight="regular" />
              )}
            </ClickableElement>
            <div className="absolute bottom-full right-0 mb-[10px] opacity-0 group-hover:opacity-100 transition-all duration-200 animate-in fade-in pointer-events-none z-10">
              <div className="relative bg-zinc-800 text-white text-xs font-medium px-2 py-1 rounded-md whitespace-nowrap shadow-md invisible md:visible">
                Load latest data
                <div className="absolute right-[10px] bottom-[-6px] w-3 h-3 bg-zinc-800 rotate-45"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Fight cards */}
        <div className="space-y-2">
          {fightPredictions.map((fight, index) => (
            <FightCard
              key={`${fight.fighter1.name}-${fight.fighter2.name}`}
              fight={fight}
              isSelected={index === selectedFightIndex}
              onClick={() => handleFightSelect(index)}
            />
          ))}
        </div>
        
        <Toast
          isVisible={isToastVisible}
          isLoading={isRefreshing}
          progress={toastProgress}
          message={toastMessage}
          onDismiss={dismissToast}
        />
      </div>
    </div>
  );
};

export default FightList; 